import { injectable } from 'tsyringe';
import { AccountType, OrderStatus, PositionType } from '../../constants/lookups';
import {
    CryptoAccountInfo,
    CryptoOrderHistory,
    CryptoOrderHistoryWithExtraInfo,
    CryptoStablecoinTransferStatus,
    CryptoStablecoinTransferStatusWithExtraInfo,
    CryptoTradingHistory,
    CryptoTradingHistoryWithExtraInfo,
    PortfolioWithExtraInfo,
    PositionWithExtraInfo,
    Quote,
} from '../../types';
import { multiply, parseCryptoSymbol } from '../../utils';
import { AccountApi } from '../AccountApi';
import { AXOS_COIN } from './lookups';
import { formatCryptoAssetWithAxx, getUuid } from './utils';

@injectable()
export class AxosAccountApi extends AccountApi {
    override async getCryptoAccountInfo(accountNumber: string): Promise<CryptoAccountInfo> {
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in sdk
            'axos/crypto/accounts',
            undefined,
            {
                params: { accountNumber: accountNumber },
            },
        );
        // Axos layer wraps data in entity array
        return response.data.entity[0];
    }

    override async getAccountCryptoBuyingPower(accountId: number, accountNumber: string): Promise<number> {
        const assets = await this.getAccountCryptoAssets(accountId, accountNumber);
        const entries: PositionWithExtraInfo[] = assets.entries;
        return entries.filter((holding) => holding.symbol.toUpperCase() === AXOS_COIN)[0]?.value || 0;
    }

    override async getAccountCryptoAssets(accountId: number, accountNumber: string): Promise<PortfolioWithExtraInfo> {
        const { data: accountAssets } = await this.sdks.middleware.get(
            // @ts-ignore: URL not in SDK
            'axos/crypto/balances',
            undefined,
            { params: { accountNumber: accountNumber || '' } },
        );
        const assetData: {
            asset: string;
            balance: number;
        }[] = accountAssets.entity?.[0].assets ? accountAssets.entity?.[0]?.assets : [];
        const assetInstruments = assetData.map((asset) => asset.asset);
        const formattedAssetInstruments = assetInstruments.map(formatCryptoAssetWithAxx).join(', ');
        const { data: instrumentQuotes } = await this.sdks.orbis.get<Quote[]>(
            // @ts-ignore: URL not in SDK
            'quotes/crypto',
            undefined,
            { params: { symbols: formattedAssetInstruments } },
        );

        const assets = assetData.map((position) => {
            const quote = instrumentQuotes.find((quote: Quote) => quote.baseCurrency === position.asset.toUpperCase());

            return {
                symbol: position.asset,
                // We use asset for logo. Column cannot have duplicate ID.
                asset: position.asset,
                lastPrice: quote?.lastPrice || 0,
                changePercent: quote?.changePercent || 0,
                value:
                    position.asset.toUpperCase() === AXOS_COIN
                        ? multiply(position.balance, 1)
                        : multiply(position.balance, quote?.lastPrice || 0),
                quantity: position.balance,

                // Dummy values, only ones that axos provides are the properties above.
                currency: '',
                option: false,
                industryValue: 0,
                industryPercentage: 0,
                price: 0,
                accountId: 0,
                type: PositionType.Cash,
                liquidationQuantity: 0,
                accountType: AccountType.Cash,
                hasRecentUpgrade: false,
                hasRecentDowngrade: false,
                long: false,
                short: false,
                daysValue: 0,
                gainPercent: 0,
                gain: 0,
                costValue: 0,
                longMargin: false,
                liquidationTransaction: '',
                longCash: false,
                dividentPayment: 0,
                sectorValue: 0,
                indValue: 0,
                groupValue: 0,
                subIndustryValue: 0,
                numAccounts: 0,
                quote: {
                    symbol: '',
                    companyName: '',
                    source: '',
                    updateTime: '',
                    tradeTime: '',
                    dividendPaymentDate: '',
                    dividendExDate: '',
                    yesterdayClose: 0,
                    openingPrice: 0,
                    strikePrice: 0,
                    rootSymbol: '',
                    dayHigh: 0,
                    dayLow: 0,
                    ask: 0,
                    bid: 0,
                    askYield: 0,
                    bidYield: 0,
                    lastPrice: 0,
                    change: 0,
                    openInterest: 0,
                    high52week: 0,
                    low52week: 0,
                    priceEarningRatio: 0,
                    changePercent: 0,
                    marketCap: 0,
                    calendarYearHigh: 0,
                    calendarYearLow: 0,
                    dividendYield: 0,
                    dividendAmount: 0,
                    dividendRate: 0,
                    totalCashAmount: 0,
                    estimatedCashAmount: 0,
                    nav: 0,
                    tradePrice: 0,
                    navPrice: 0,
                    navChange: 0,
                    navYesterdayPrice: 0,
                    askSize: 0,
                    bidSize: 0,
                    askSizeFractional: 0,
                    bidSizeFractional: 0,
                    minAskSize: 0,
                    minBidSize: 0,
                    tradeSize: 0,
                    putOrCall: '',
                    precision: 0,
                    delayed: false,
                    option: false,
                    expirationDate: '',
                    notPermissioned: false,
                    mutualFund: false,
                    bond: false,
                    marketDescription: '',
                    call: false,
                    marketCapClassification: '',
                    put: false,
                    volume: 0,
                    isin: 0,
                    exchange: 0,
                    averageVolume30: 0,
                    bidRegion: {
                        code: '',
                        description: '',
                    },
                    askRegion: {
                        code: '',
                        description: '',
                    },
                    notFound: false,
                    baseCurrency: '',
                    crypto: true,
                },
                fullName: '',
                accountNumber: '',
                weight: 0,
                accountWeight: 0,
                marketCapClassification: '',
                companyName: '',
                dividendAmount: 0,
                dividendYield: 0,
                dividendExDate: '',
                dividendPaymentDate: '',
                inventory: false,
                todaysGain: 0,
                todaysGainPercent: 0,
            };
        });
        return {
            // Axos API returns assets even when quantity is 0. They requested to filter this for the time being. Might change when more coins are available.
            entries: assets.filter((asset) => asset.quantity !== 0),
            totalGainPct: 0,
            containsShort: false,
            homogeneous: false,
            total: 0,
            totalGain: 0,
            containsEquities: false,
            currency: '',
            containsOptions: false,
            totalCost: 0,
            totalDaysValue: 0,
            totalTodaysGain: 0,
            totalTodaysGainPercent: 0,
        };
    }

    override async getAccountCryptoAccountOrderHistory(
        accountNumber: string,
    ): Promise<CryptoOrderHistoryWithExtraInfo[]> {
        const [availableCoins, response] = await Promise.all([
            this.quoteApi.getAvailableCoin(),
            this.sdks.middleware.get<{ entity: CryptoOrderHistory[] }>(
                // @ts-ignore: URL not in sdk
                'axos/crypto/trading/orders/history',
                undefined,
                {
                    params: { accountNumber: accountNumber },
                },
            ),
        ]);

        if (response.data.entity) {
            // Axos layer wraps data within entity object
            return response.data.entity.map((item: any) => {
                const coin = availableCoins.find(
                    (availableCoin) => availableCoin.symbol === parseCryptoSymbol(item.instrument).symbol,
                );
                return {
                    ...item,
                    symbol: item.instrument,
                    nameWithIcon: `${coin?.name} (${coin?.icon})` || '',
                };
            });
        } else {
            return [];
        }
    }

    override async getAccountCryptoAccountTradingHistory(
        accountNumber: string,
    ): Promise<CryptoTradingHistoryWithExtraInfo[]> {
        const [availableCoins, response] = await Promise.all([
            this.quoteApi.getAvailableCoin(),
            this.sdks.middleware.get<{ entity: CryptoTradingHistory[] }>(
                // @ts-ignore: URL not in sdk
                'axos/crypto/trading/trades/history',
                undefined,
                {
                    params: { accountNumber: accountNumber },
                },
            ),
        ]);
        if (response.data.entity) {
            // Axos layer wraps data within entity object
            return response.data.entity.map((item: any) => {
                const coin = availableCoins.find(
                    (availableCoin) => availableCoin.symbol === parseCryptoSymbol(item.instrument).symbol,
                );
                return {
                    ...item,
                    symbol: item.instrument,
                    nameWithIcon: `${coin?.name} (${coin?.icon})` || '',
                };
            });
        } else {
            return [];
        }
    }

    override async getAccountCryptoOpenOrders(accountNumber: string): Promise<CryptoOrderHistoryWithExtraInfo[]> {
        const status = [OrderStatus.Open, OrderStatus.PendingCancel];
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in SDK
            'axos/crypto/trading/orders',
            undefined,
            {
                params: {
                    accountNumber: accountNumber,
                    status: status.join(','),
                },
            },
        );
        if (response.data.entity) {
            return response.data.entity.map((item: CryptoTradingHistory) => ({
                ...item,
                symbol: item.instrument,
                asset: item.instrument,
            }));
        } else {
            return [];
        }
    }

    override async purchaseCryptoStablecoins(accountNumber: string, amount: number): Promise<{ reason: string }> {
        // @ts-ignore: URL not in sdk
        const response = await this.sdks.middleware.post('axos/crypto/transfers/stablecoins/purchase', {
            requestId: getUuid(),
            requests: [{ accountNumber: accountNumber, amount: amount }],
        });

        // Axos layer wraps data within entity array. This endpoints supports batch requests, but for this instance, we only send one, so we take the first object in entity array.
        return response.data.entity[0];
    }

    override async redeemCryptoStablecoins(accountNumber: string, amount: number): Promise<{ reason: string }> {
        // @ts-ignore: URL not in sdk
        const response = await this.sdks.middleware.post('axos/crypto/transfers/stablecoins/redemption', {
            requestId: getUuid(),
            requests: [{ accountNumber: accountNumber, amount: amount }],
        });

        // Axos layer wraps data within entity array. This endpoints supports batch requests, but for this instance, we only send one, so we take the first object entity array.
        return response.data.entity[0];
    }

    override async getCryptoAccountStablecoinTransferStatuses(
        accountNumber: string,
    ): Promise<CryptoStablecoinTransferStatusWithExtraInfo[]> {
        const response = await this.sdks.middleware.get<{ entity: CryptoStablecoinTransferStatus[] }>(
            // @ts-ignore: URL not in sdk
            'axos/crypto/transfers',
            undefined,
            {
                params: { accountNumber: accountNumber, pageStart: 0, pageSize: 30 },
            },
        );
        if (response.data.entity) {
            // Axos layer wraps data within entity object.
            return response.data.entity.map((item: any) => ({
                ...item,
                symbol: item.asset,
            }));
        } else {
            return [];
        }
    }

    override async getCryptoAccountAvailablePdfStatementDates(
        accountNumber: string,
    ): Promise<{ from: Date; to: Date }> {
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in sdk
            'axos/crypto/accounts/account-reports/info',
            undefined,
            { params: { accountNumber: accountNumber } },
        );
        const availableDates: { from: Date; to: Date } = response.data.entity[0].reportsAvailability;
        return availableDates;
    }

    override async generateCryptoAccountPdfStatement(accountNumber: string, date: string): Promise<Blob> {
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in sdk
            'axos/crypto/accounts/account-reports/pdf',
            undefined,
            {
                responseType: 'blob',
                params: { reportDate: date, accountNumber: accountNumber },
            },
        );
        return response.data;
    }

    override async getCryptoAccountOrdersCsv(accountNumber: string): Promise<Blob> {
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in sdk
            'axos/crypto/trading/orders/csv',
            undefined,
            {
                responseType: 'blob',
                params: { accountNumber: accountNumber },
            },
        );
        return response.data;
    }

    override async getCryptoAccountTransfersCsv(accountNumber: string): Promise<Blob> {
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in sdk
            'axos/crypto/transfers/csv',
            undefined,
            {
                responseType: 'blob',
                params: { accountNumber: accountNumber },
            },
        );
        return response.data;
    }
}
